import CircleIcon from '@mui/icons-material/Circle';
import { Timeline } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ActivityLogDTO,
  getActivityLogs,
  getActivityLogsByEntity,
  getLeadById,
  ILead,
} from 'wisestep-api';
import {
  ScrollableButtonTabs,
  WsColors,
  WsTimeLine,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  formatDateInDayMonthYearTime,
} from 'wisestep-base';

import { AccessControl } from '../../../../components';
import { NotesParent } from '../../../../components/communicationBar/Notes';
import {
  IBreadCrumbsList,
  WsBreadCrumbs,
} from '../../../../components/WsBreadCrumbs/WsBreadCrumbs';
import { WsTabs } from '../../../../components/WsTabs';
import ComposeNote from '../../../../features/clientDetails/components/notes/ComposeNote';
import { LeadAccess, useLeadAuthz } from '../../../../hooks/useLeadAuthz';
import { defaultLeadDTO, tabsList } from '../../helper/leadHelper';

import LeadDetailsHeader from './LeadDetailsHeader';
import LeadInfoTab from './LeadInfoTab';

const getLeadBreadCrumbs = (title: string): IBreadCrumbsList[] => [
  { label: 'Leads', url: '/leads' },
  { label: title, url: '' },
];

const LeadDetails = () => {
  const [params] = useSearchParams();
  const leadId: string = params.get('leadId') ?? '';
  const queryData = params.get('d') ?? '';
  const tabIndex: number = parseInt(params.get('tabIndex') ?? '0');
  const [lead, setLead] = useState<ILead>(defaultLeadDTO);
  const [selectedTab, setSelectedTab] = useState<number>(tabIndex);
  const [activityData, setActivityData] = useState<ActivityLogDTO[]>([]);

  const isAuthorizedViewLeadDetails = useLeadAuthz(LeadAccess.VIEW_LEAD_DETAILS);

  useEffect(() => {
    (async () => {
      try {
        const response = await getLeadById(leadId);
        response.data && setLead(response.data);
      } catch (e) {
        console.error('error fetching lead info', e);
      }
    })();
  }, [leadId]);

  useEffect(() => {
    const fetchActivityLogs = async () => {
      if (!leadId) return;
      try {
        const [contextLogs, entityLogs] = await Promise.all([
          getActivityLogs(leadId),
          getActivityLogsByEntity(leadId),
        ]);

        const combined = [...contextLogs.data, ...entityLogs.data];
        const uniqueLogs = combined.filter(
          (log, index, arr) => arr.findIndex((l) => l.id === log.id) === index
        );
        setActivityData(uniqueLogs);
      } catch (err) {
        console.error('Failed to fetch activity logs', err);
      }
    };

    fetchActivityLogs();
  }, [leadId]);

  const activityListItems = activityData.map((log: ActivityLogDTO) => ({
    activity: (
      <Timeline>
        <Grid container>
          <Grid item xs={12}>
            <WsTypography color={WsTypoColors.TERTIARY}>
              {`At ${formatDateInDayMonthYearTime(log.createdOn.toString())} by ${
                log.createdByName
              }`}
            </WsTypography>
          </Grid>
          <Grid item xs={12}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {`${log.field} Category -> ${log.description}`}
            </WsTypography>
          </Grid>
        </Grid>
      </Timeline>
    ),
    leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize="small" />,
  }));

  const breadCrumbsList: IBreadCrumbsList[] = getLeadBreadCrumbs(lead.leadName ?? 'Lead Details');

  return (
    <>
      <WsBreadCrumbs breadCrumbsList={breadCrumbsList ?? []} maxItems={4} />
      <Box>
        <LeadDetailsHeader setLead={setLead} lead={lead} queryData={queryData} />
        {isAuthorizedViewLeadDetails ? (
          <>
            <Box borderRadius={1.5} sx={{ bgcolor: WsColors.White }} mt={2} mb={1}>
              <ScrollableButtonTabs
                scrollList={tabsList}
                getTabIndex={setSelectedTab}
                defaultValue={selectedTab}
              />
            </Box>
            <Box borderRadius={1.5} mt={2} mb={1}>
              {selectedTab == 1 && <LeadInfoTab leadInfo={lead} />}
              {selectedTab == 0 && (
                <WsTabs>
                  <WsTabs.Item label="Notes">
                    <ComposeNote
                      parent={NotesParent.CLIENTCONTACT}
                      parentId={lead?.client?.id ?? ''}
                      isClient={false}
                      clientContactId={lead?.clientContact?.id ?? ''}
                    />
                  </WsTabs.Item>
                  <WsTabs.Item label="Activities">
                    <Grid item xs={12} borderRadius={2} pl={2} sx={{ background: WsColors.White }}>
                      <Timeline>
                        <Box ml="-100%">
                          <WsTimeLine
                            activityList={activityListItems}
                            timeLineDotVariant="outlined"
                          />
                        </Box>
                      </Timeline>
                    </Grid>
                  </WsTabs.Item>
                </WsTabs>
              )}
            </Box>
          </>
        ) : (
          <Box mt={2}>
            <AccessControl />
          </Box>
        )}
      </Box>
    </>
  );
};

export default LeadDetails;
